// Calendário de eventos do mês (fins de semana com Rapid Respawn, Double Loot, Double Skill e Double Experience).
// Visual de calendário festivo: bandeirinhas, confete e as cores de cada evento. CSS em client/css-parts/quests.css (prefixo .ev-).
import { useState } from 'preact/hooks';
import { EVENT_INFO, EVENT_LOOT_MULT, EVENT_RESPAWN_DIV, EVENT_SKILL_MULT, EVENT_XP_MULT, MONTH_THEME, activeEvents, eventsOn, type EventKind } from '../../../../shared/src/game/events';

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const WD = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const COLOR: Record<EventKind, string> = { respawn: '#3c8d3c', loot: '#c9a227', skill: '#2f7fd0', xp: '#b3402f' };

const ICON: Record<EventKind, string> = { respawn: '↻', loot: '◆', skill: '✦', xp: '★' };
const SHORT: Record<EventKind, string> = { respawn: 'Respawn', loot: '2x Loot', skill: '2x Skill', xp: '2x XP' };
const MULT: Record<EventKind, string> = { respawn: `${EVENT_RESPAWN_DIV}x`, loot: `${EVENT_LOOT_MULT}x`, skill: `${EVENT_SKILL_MULT}x`, xp: `${EVENT_XP_MULT}x` };
const WEEK: Record<EventKind, string> = { respawn: '1º fim de semana', loot: '2º fim de semana', skill: '3º fim de semana', xp: '4º fim de semana' };

const dur = (ms: number) => {
  const h = Math.max(0, Math.floor(ms / 3600_000)), m = Math.max(0, Math.floor((ms % 3600_000) / 60_000));
  return h >= 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h${String(m).padStart(2, '0')}`;
};

export function Events() {
  const now = new Date(Date.now() - 3 * 3600_000);   // horário de Brasília
  const [off, setOff] = useState(0);
  const [help, setHelp] = useState(false);
  const base = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + off, 1));
  const y = base.getUTCFullYear(), m = base.getUTCMonth();
  const days = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const first = base.getUTCDay();
  const today = off === 0 ? now.getUTCDate() : -1;
  const active = activeEvents();
  const cells: Array<number | null> = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);
  // próximo fim de semana com evento (a partir de hoje)
  let next: { d: Date; ev: EventKind[]; k: number } | null = null;
  for (let k = 0; k < 40 && !next; k++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + k));
    const ev = eventsOn(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    if (ev.length) next = { d, ev, k };
  }
  const nextLabel = next ? (next.d.getUTCDate() === now.getUTCDate() && next.d.getUTCMonth() === now.getUTCMonth() ? 'hoje'
    : `${WD[next.d.getUTCDay()]}, ${next.d.getUTCDate()}/${String(next.d.getUTCMonth() + 1).padStart(2, '0')}`) : '';
  // evento ativo termina na virada de domingo para segunda (Brasília); o próximo começa à 00h do dia achado
  const endsIn = active.length ? Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + (now.getUTCDay() === 6 ? 2 : 1)) - now.getTime() : 0;
  const startsIn = !active.length && next ? next.d.getTime() - now.getTime() : 0;
  const monthCount = Array.from({ length: days }, (_, i) => eventsOn(y, m, i + 1).length ? 1 : 0).reduce<number>((a, b) => a + b, 0);
  const shown = active.length ? active : next?.ev ?? [];

  return (
    <div class={'ev' + (active.length ? ' ev-live' : '')}>
      <div class="ev-head">
        <div class="ev-bunting">{Array.from({ length: 14 }, (_, i) => <i key={i} />)}</div>
        <div class="ev-cal" title={`${MONTHS[now.getUTCMonth()]} de ${now.getUTCFullYear()}`}>
          <small>{MONTHS[now.getUTCMonth()]!.slice(0, 3).toUpperCase()}</small>
          <b>{now.getUTCDate()}</b>
        </div>
        <div class="ev-head-txt">
          <div class="ev-title">Calendário de Eventos
            <button class="ev-help" onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
          <div class="ev-sub">Todo fim de semana é festa em Noxara: bônus em dobro para quem estiver caçando.</div>
        </div>
      </div>

      {help && (
        <div class="ev-steps">
          <div><i>📅</i><b>Fins de semana</b><small>Os eventos valem no sábado e no domingo, o dia inteiro (horário de Brasília).</small></div>
          <div><i>🔁</i><b>Um por semana</b><small>1º Rapid Respawn, 2º Double Loot, 3º Double Skill, 4º Double XP.</small></div>
          <div><i>🎉</i><b>5º fim de semana</b><small>Se o mês tiver um 5º fim de semana, todos os eventos valem juntos!</small></div>
        </div>
      )}

      <div class={'ev-now' + (active.length ? ' on' : '')}>
        <div class="ev-now-badge">{active.length ? 'AO VIVO' : next ? 'EM BREVE' : '—'}</div>
        <div class="ev-now-txt">
          {active.length
            ? <><small>Acontecendo agora</small><b>{active.map((e) => EVENT_INFO[e].name).join(' + ')}</b></>
            : next ? <><small>Próximo evento · {nextLabel}</small><b>{next.ev.map((e) => EVENT_INFO[e].name).join(' + ')}</b></> : <small>Sem eventos marcados</small>}
          <span class="ev-now-chips">{shown.map((e) => <i key={e} style={{ background: COLOR[e] }}>{ICON[e]} {MULT[e]}</i>)}</span>
        </div>
        <div class="ev-count">
          {active.length ? <><small>termina em</small><b>{dur(endsIn)}</b></> : next ? <><small>começa em</small><b>{dur(startsIn)}</b></> : null}
        </div>
      </div>

      <div class="ev-month">
        <button class="ev-nav" disabled={off <= 0} onClick={() => setOff(off - 1)} aria-label="Mês anterior">‹</button>
        <div><b>{MONTHS[m]} {y}</b><small>🎭 tema: {MONTH_THEME[m]} · {monthCount} dias de evento</small></div>
        <button class="ev-nav" disabled={off >= 2} onClick={() => setOff(off + 1)} aria-label="Próximo mês">›</button>
      </div>

      <div class="ev-grid">
        {WD.map((d, i) => <span key={d} class={'ev-wd' + (i === 0 || i === 6 ? ' we' : '')}>{d}</span>)}
        {cells.map((d, i) => {
          if (!d) return <span key={'e' + i} class="ev-day empty" />;
          const ev = eventsOn(y, m, d);
          const past = off === 0 && d < today;
          return (
            <span key={d} class={'ev-day' + (d === today ? ' today' : '') + (past ? ' past' : '') + (ev.length ? ' has' : '') + (ev.length === 4 ? ' all' : '') + (i % 7 === 0 || i % 7 === 6 ? ' we' : '')}
              style={ev.length === 1 ? { '--ev-c': COLOR[ev[0]!] } as unknown as Record<string, string> : undefined}
              title={ev.map((e) => `${EVENT_INFO[e].name}: ${EVENT_INFO[e].desc}`).join('\n')}>
              <b>{d}{d === today && <em>hoje</em>}</b>
              {ev.length === 4 ? <i class="all">🎉 Todos!</i> : ev.map((e) => <i key={e} style={{ background: COLOR[e] }}>{ICON[e]} {SHORT[e]}</i>)}
            </span>
          );
        })}
      </div>

      <div class="ev-legend">
        {(Object.keys(EVENT_INFO) as EventKind[]).map((e) => (
          <div key={e} class={'ev-card' + (active.includes(e) ? ' on' : '')} style={{ '--ev-c': COLOR[e] } as unknown as Record<string, string>}>
            <span class="ev-ico">{ICON[e]}</span>
            <div><b>{EVENT_INFO[e].name}</b><small>{EVENT_INFO[e].desc}</small><em>{WEEK[e]}</em></div>
            <strong class="ev-mult">{MULT[e]}</strong>
          </div>
        ))}
      </div>
      <p class="ev-note">Os eventos valem no sábado e no domingo, o dia inteiro (horário de Brasília). 1º fim de semana Rapid Respawn, 2º Double Loot, 3º Double Skill, 4º Double XP; um 5º fim de semana no mês tem todos juntos. Todo dia também há a criatura e o boss do dia.</p>
    </div>
  );
}
